import { PlayerRef, Player as RemotionPlayer, RenderPoster } from '@remotion/player';
import { useCallback, useEffect, useMemo, useRef, useSyncExternalStore } from 'react';
import { AbsoluteFill } from 'remotion';
import { isEmpty } from 'radash';
import MainPlayer from '../MainPlayer';
import { ResponsiveContainer } from '../../../../components/atoms/ResponsiveContainer';
import { getClipPlayerElementId } from '../../../../Pages/Clip/ClipPlayer/ClipPlayerUtils';
import { alignedRecordings } from '../stores/alignedRecordings';
import CaptionsMenu from '../../components/Captions/CaptionsMenu';
import { RemotionPlayerContext } from './context/RemotionPlayerContext';
import { usePreloadRemotionAssets } from './hooks/usePreloadRemotionAssets';
import { getPlayerConfigIntro } from './utils/playerConfigGetters';
import { useUpdateCurrentSrtIndex } from './hooks/useUpdateCurrentSrtIndex';
import { COMPOSITION_DIMENSIONS, FPS_24, VIDEO_QUALITY } from '@/App/remotion/constants';
import Loader from '@/components/atoms/Loader';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import { classnames } from '@/libs/utils';
import ClipHeader from '@/components/molecules/ClipHeader';
import ClipCustomizerMenu from '@/Pages/Clip/ClipPlayer/ClipCustomizer/ClipCustomizerMenu';
import featureFlagStore from '@/stores/featureFlagStore';
import { FeatureFlagKeys } from '@/services/featureFlag';
import FullTranscript from '@/components/molecules/Transcript/FullTranscript';
import useTranscriptPage from '@/Pages/TranscriptPage/useTranscriptPage';
import { changeCaptionsPosition } from '@/stores/clip';
import { ClipNewTimeline } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline';

export default function ClipPlayerV2WithNewTimeline({ playerClasses = '' }: { playerClasses?: string }) {
  const remotionPlayerRef = useRef<PlayerRef>(null);
  const { clipId, clipData, playerStore } = useClipsContext();
  const { createClip } = useTranscriptPage();
  const playerConfig = clipData.asset_metadata.player_config;

  usePreloadRemotionAssets({
    playerConfig
  });

  useUpdateCurrentSrtIndex({
    playerConfig,
    remotionPlayerRef: remotionPlayerRef
  });

  const featureFlags = useSyncExternalStore(featureFlagStore.subscribe, featureFlagStore.getSnapshot);
  const showPlayerFrames = featureFlags[FeatureFlagKeys.Use_CL_Show_Player_Frames];
  const { compositionHeight, compositionWidth } = useMemo(() => {
    const videoQuality = VIDEO_QUALITY.FULL_HD;
    const compositionHeight = COMPOSITION_DIMENSIONS[clipData.asset_metadata.size][videoQuality].HEIGHT;
    const compositionWidth = COMPOSITION_DIMENSIONS[clipData.asset_metadata.size][videoQuality].WIDTH;

    return {
      compositionHeight,
      compositionWidth
    };
  }, [clipData.asset_metadata.size]);

  useEffect(() => {
    return () => {
      if (alignedRecordings.getSnapshot().length !== 0) {
        alignedRecordings.set(() => []);
      }
    };
  }, []);

  const renderPoster: RenderPoster = useCallback(({ height, width, isBuffering }) => {
    if (isBuffering) {
      return (
        <AbsoluteFill
          className="absolute inset-0 overflow-hidden bg-slate-100"
          data-testid="composite-player-loader"
          id="composite-player-loader"
        >
          <div className="absolute inset-0 flex animate-pulse items-center justify-center overflow-hidden bg-gray-200">
            <Loader />
          </div>
        </AbsoluteFill>
      );
    }

    return (
      <AbsoluteFill style={{ backgroundColor: 'gray' }}>
        Click to play! ({height}x{width})
      </AbsoluteFill>
    );
  }, []);

  const onCreateClip = useCallback(
    (start: number, end: number) => {
      return createClip(start, end, { edits: clipData.asset_metadata.edits, deletes: clipData.asset_metadata.deletes });
    },
    [clipData.asset_metadata.deletes, clipData.asset_metadata.edits, createClip]
  );

  const onOutlineRelease = useCallback(
    positions => {
      changeCaptionsPosition({
        clipId,
        caption_positions: {
          x: positions.left as any,
          y: positions.top as any,
          width: positions.width as any,
          height: positions.height as any
        },
        skipUpdate: false
      });
    },
    [clipId]
  );

  const playMainSectionFromTime = useCallback(
    (time: number, shouldPlay?: boolean) => {
      const isPlaying = remotionPlayerRef.current?.isPlaying;
      if (shouldPlay || !isPlaying) {
        remotionPlayerRef.current?.dispatchPlay();
      } else {
        remotionPlayerRef.current?.dispatchPause();
      }
      const intro = getPlayerConfigIntro(playerConfig);
      const introDurationFrames = intro ? intro.toFrame - intro.toFrame : 0;
      remotionPlayerRef.current?.seekTo(introDurationFrames + Math.ceil(time * FPS_24));
    },
    [playerConfig]
  );

  return (
    <RemotionPlayerContext.Provider value={{ playerRef: remotionPlayerRef.current }}>
      <div
        id={getClipPlayerElementId(clipId)}
        className={'flex h-full w-full grow flex-col items-center justify-center overflow-hidden px-3'}
      >
        <div className="flex h-full w-full flex-row items-center justify-between">
          <div className="relative h-full w-[calc(34rem-10px)] py-4">
            <FullTranscript
              playerStore={playerStore}
              videoAssetId={clipId}
              title="Recording"
              onCreateClip={onCreateClip}
              onTimeUpdate={playMainSectionFromTime}
              onPause={() => {
                remotionPlayerRef.current?.pause();
              }}
              fullClipOffset={0}
            />
          </div>
          <div className={'flex h-full w-[calc(100%-34rem)] flex-col'}>
            <ClipHeader />
            <div
              className={classnames(
                'flex h-full w-full items-center justify-center overflow-hidden rounded-md p-4 pr-24',
                playerClasses
              )}
            >
              <ResponsiveContainer
                id="player-main-section"
                compositionHeight={compositionHeight}
                compositionWidth={compositionWidth}
                parentHeight={'80%'}
                parentWidth={'80%'}
              >
                {/* ! TODO: @AshwinBhatkal to handle the state updates better */}
                {playerConfig && !isEmpty(playerConfig) && (
                  <RemotionPlayer
                    component={MainPlayer}
                    durationInFrames={playerConfig.durationInFrames || 0}
                    compositionWidth={compositionWidth}
                    compositionHeight={compositionHeight}
                    fps={FPS_24}
                    ref={remotionPlayerRef}
                    clickToPlay={false}
                    spaceKeyToPlayOrPause={false}
                    controls={showPlayerFrames}
                    inputProps={{
                      ...playerConfig,
                      showPlayerFrames,
                      onOutlineRelease,
                      CaptionsMenu
                    }}
                    style={{
                      width: '100%'
                    }}
                    showVolumeControls
                    renderPoster={renderPoster}
                    showPosterWhenBuffering
                  />
                )}
              </ResponsiveContainer>
              <ClipCustomizerMenu
                isFullRecordingEdit={true}
                onTimeUpdate={playMainSectionFromTime}
                useNewRecordingsPlayer
              />
            </div>
          </div>
        </div>
        <ClipNewTimeline />
      </div>
    </RemotionPlayerContext.Provider>
  );
}
